/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Teams.
 *
 * Teams replace the old manager tree. The difference that matters to an admin:
 * someone can be in more than one team, so "everyone on the support desk" is
 * expressible without inventing a reporting line that does not exist.
 *
 * Sub-teams roll up, which is how the previous manager-sees-their-reports
 * behaviour survives the migration.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  ClearRounded,
  DeleteOutline,
  GroupsRounded,
  PersonAddAlt1Rounded,
  SearchRounded,
  SubdirectoryArrowRightRounded,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import { useCapability } from 'auth/usePermission';
import { notifyPermissionsChanged } from 'auth/PermissionsProvider';
import StyledAccessDenied from 'components/StyledAccessDenied';
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

/** Past this size, "my team" scopes start resolving over a lot of people. */
const LARGE_TEAM = 200;

const displayName = (u) =>
  `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username || u.email;

const initials = (u) =>
  displayName(u).split(' ').filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase()).join('');

export default function Teams() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  const canManage = useCapability('MANAGE_TEAMS');

  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '', parent_team_id: '' });
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const [teamsRes, usersRes] = await Promise.all([
        axiosInstance.get('/teams'),
        axiosInstance.get('/users/'),
      ]);
      setTeams(teamsRes.data);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.items ?? []);
      setSelectedId((current) => current ?? teamsRes.data[0]?.id ?? null);
    } catch {
      setToast({ severity: 'error', message: 'Could not load teams.' });
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  const loadMembers = useCallback(async (teamId) => {
    if (!teamId) { setMembers([]); return; }
    try {
      const { data } = await axiosInstance.get(`/teams/${teamId}/members`);
      setMembers(data);
    } catch {
      setMembers([]);
    }
  }, [axiosInstance]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMembers(selectedId); }, [selectedId, loadMembers]);

  const selected = useMemo(
    () => teams.find((t) => t.id === selectedId) ?? null,
    [teams, selectedId]
  );

  const searching = search.trim().length > 0;

  // Client-side: the list is short, so a request per keystroke would add
  // latency and nothing else.
  const visibleTeams = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) =>
      `${t.name} ${t.description ?? ''}`.toLowerCase().includes(q)
    );
  }, [teams, search]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);

  const createTeam = async () => {
    if (!draft.name.trim()) return;
    try {
      const { data } = await axiosInstance.post('/teams', {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        parent_team_id: draft.parent_team_id || null,
      });
      setCreating(false);
      setDraft({ name: '', description: '', parent_team_id: '' });
      await load();
      setSelectedId(data.id);
      notifyPermissionsChanged();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setToast({
        severity: 'error',
        message: detail?.message ?? 'Could not create the team.',
      });
    }
  };

  const addMember = async (user) => {
    if (!user) return;
    try {
      await axiosInstance.post(`/teams/${selectedId}/members`, {
        user_ids: [user.id], membership: 'member',
      });
      await Promise.all([loadMembers(selectedId), load()]);
      notifyPermissionsChanged();
    } catch {
      setToast({ severity: 'error', message: 'Could not add that person.' });
    }
  };

  const removeMember = async (userId) => {
    try {
      await axiosInstance.delete(`/teams/${selectedId}/members/${userId}`);
      await Promise.all([loadMembers(selectedId), load()]);
      notifyPermissionsChanged();
    } catch {
      setToast({ severity: 'error', message: 'Could not remove that person.' });
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <StyledAccessDenied />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isV2 ? (
          <PageHeader
            title="Teams"
            summary="Group people so a role can grant access to a whole team's records. Sub-teams roll up."
            actions={(
              <MDButton variant="gradient" color="info" onClick={() => setCreating(true)}>
                <AddRounded sx={{ mr: 0.75 }} /> New team
              </MDButton>
            )}
          />
        ) : (
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={3} flexWrap="wrap">
          <Box>
            <Typography variant="h4" fontWeight={700} lineHeight={1.2}>
              Teams
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Group people so a role can grant access to a whole team&apos;s records.
              Sub-teams roll up.
            </Typography>
          </Box>
          <MDButton variant="gradient" color="info" onClick={() => setCreating(true)}>
            <AddRounded sx={{ mr: 0.75 }} /> New team
          </MDButton>
        </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : !teams.length ? (
          <Box sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '12px' }}>
            <GroupsRounded sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="h6" gutterBottom>No teams yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Teams are optional. Roles set to “Own records” or “Everyone” work
              without them.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" gap={2.5} alignItems="flex-start" flexWrap="wrap">
            {/* ------------------------------------------------- team list */}
            <Box sx={{ width: 320, flexShrink: 0, display: 'grid', gap: 1 }}>
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search teams"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                        <ClearRounded sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {!visibleTeams.length && (
                <Box sx={{ px: 1.5, py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No teams match &ldquo;{search}&rdquo;.
                  </Typography>
                </Box>
              )}

              {visibleTeams.map((team) => {
                const active = team.id === selectedId;
                return (
                  <Box
                    key={team.id}
                    component="button"
                    type="button"
                    onClick={() => setSelectedId(team.id)}
                    sx={{
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      // Depth indent makes the hierarchy legible without a full
                      // tree control. Suppressed while filtering: a filtered
                      // list is not a tree, and indenting a child whose parent
                      // was filtered out implies a nesting that is not on screen.
                      pl: searching ? 1.5 : 1.5 + (team.depth ?? 0) * 2,
                      pr: 1.5,
                      py: 1.25,
                      border: '1px solid',
                      borderColor: active ? 'info.main' : 'divider',
                      borderRadius: '10px',
                      backgroundColor: active ? 'rgba(21,101,200,0.08)' : 'background.paper',
                      transition: 'border-color .15s, background-color .15s',
                      '&:hover': { borderColor: 'info.main' },
                    }}
                  >
                    {!searching && team.depth > 0 && (
                      <SubdirectoryArrowRightRounded
                        sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                      />
                    )}
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={600} noWrap>{team.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {team.member_count} {team.member_count === 1 ? 'member' : 'members'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* ----------------------------------------------- team detail */}
            <Box
              sx={{
                flex: 1, minWidth: 420,
                border: '1px solid', borderColor: 'divider',
                borderRadius: '12px', backgroundColor: 'background.paper',
                p: 2.5,
              }}
            >
              {selected ? (
                <>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={2}>
                    <Box minWidth={0}>
                      <Typography variant="h6" fontWeight={700}>{selected.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selected.description || 'No description'}
                      </Typography>
                    </Box>
                    {members.length > 0 && (
                      <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 11 } }}>
                        {members.map((m) => (
                          <Avatar key={m.user_id}>{initials(m)}</Avatar>
                        ))}
                      </AvatarGroup>
                    )}
                  </Box>

                  {members.length > LARGE_TEAM && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Large teams make “My team” scopes more expensive to evaluate.
                      Consider splitting this into sub-teams.
                    </Alert>
                  )}

                  <Autocomplete
                    size="small"
                    options={users.filter((u) => !memberIds.has(u.id))}
                    getOptionLabel={displayName}
                    onChange={(_e, value) => addMember(value)}
                    value={null}
                    blurOnSelect
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Add someone to this team" />
                    )}
                    sx={{ maxWidth: 380, mb: 2 }}
                  />

                  <Box display="grid" gap={0.5}>
                    {members.map((m) => (
                      <Box
                        key={m.user_id}
                        display="flex" alignItems="center" gap={1.5}
                        sx={{
                          px: 1.5, py: 1,
                          border: '1px solid', borderColor: 'divider', borderRadius: '10px',
                        }}
                      >
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 700 }}>
                          {initials(m)}
                        </Avatar>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {displayName(m)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {m.username}
                          </Typography>
                        </Box>
                        {m.membership === 'lead' && (
                          <Chip size="small" label="Lead"
                            sx={{ height: 20, fontSize: 10.5, fontWeight: 600 }} />
                        )}
                        <Tooltip title="Remove from this team">
                          <span>
                            <IconButton size="small" onClick={() => removeMember(m.user_id)}>
                              <DeleteOutline sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ))}
                    {!members.length && (
                      <Box textAlign="center" py={5}>
                        <PersonAddAlt1Rounded sx={{ fontSize: 34, opacity: 0.25 }} />
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          Nobody is in this team yet.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a team to see its members.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </MDBox>

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New team</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="Team name"
            placeholder="e.g. Support Desk"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth size="small" label="Description (optional)"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select fullWidth size="small" label="Parent team (optional)"
            value={draft.parent_team_id}
            onChange={(e) => setDraft({ ...draft, parent_team_id: e.target.value })}
            helperText="A role scoped to “My team & sub-teams” sees everything beneath its team."
          >
            <MenuItem value="">None — top level</MenuItem>
            {teams.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="text" color="dark" onClick={() => setCreating(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient" color="info" onClick={createTeam}
            disabled={!draft.name.trim()}
          >
            Create team
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
